import JobPost from "~/models/job-post";
import Page from "~/models/page";
import Url from "~/models/url";
import feedContent from "~/helpers/feed-content";

const structuredData = (page: Page, jobPost?: JobPost) => {
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      url: process.env.GATSBY_SITE_URL,
      logo: `${process.env.GATSBY_SITE_URL}/images/icon.png`,
    },
  ] as any;

  // Breadcrumb
  const breadcrumb = page.breadcrumb?.map((url: Url, index: number) => {
    return {
      "@type": "ListItem",
      position: index + 1,
      "@id": `${process.env.GATSBY_SITE_URL}${url.pathname}`,
      name: url.name,
      item: `${process.env.GATSBY_SITE_URL}${url.pathname}`,
    };
  });

  if (breadcrumb && breadcrumb.length) {
    structuredData.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumb,
    });
  }

  // Article
  let match = page.url.pathname.match(/^\/[^\/]+/);
  let directory = match ? match[0] : "";

  if (
    ["/articles"].indexOf(directory) != -1 &&
    page.url.pathname.split("/").length > 3
  ) {
    if (page.faq) {
      const mainEntity = [] as any;

      page.faq.items.forEach((faq: any) => {
        mainEntity.push({
          "@type": "Question",
          name: faq[0],
          acceptedAnswer: {
            "@type": "Answer",
            text: faq[1],
          },
        });
      });

      structuredData.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity,
      });
    }

    const authors = [];

    if (page.contributors.length > 0) {
      page.contributors.forEach((contributor) => {
        authors.push({
          "@type": "Person",
          name: contributor.name,
          url: contributor.website || "",
        });
      });
    }

    authors.push({
      "@type": "Organization",
      name: "unprinted",
      url: process.env.GATSBY_SITE_URL,
    });

    structuredData.push({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: page.metaTitle,
      description: page.description,
      author: authors,
      url: `${process.env.GATSBY_SITE_URL}${page.url.pathname}`,
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `${process.env.GATSBY_SITE_URL}${page.url.pathname}`,
      },
      image: [
        {
          "@type": "ImageObject",
          url:
            page.imageUrls?.cover?.publicURL ||
            `${process.env.GATSBY_SITE_URL}/images/ogp.png`,
          width: "2400px",
          height: "1260px",
        },
        {
          "@type": "ImageObject",
          url:
            page.imageUrls?.square?.publicURL ||
            `${process.env.GATSBY_SITE_URL}/images/ogp.png`,
          width: "1260px",
          height: "1260px",
        },
      ],
      datePublished: page.releasedAt?.toISO() || page.updatedAt.toISO(),
      dateModified: page.updatedAt.toISO(),
      publisher: {
        "@type": "Organization",
        name: "unprinted",
        url: process.env.GATSBY_SITE_URL,
      },
    });
  }

  // News
  if (
    ["/news"].indexOf(directory) != -1 &&
    page.url.pathname.split("/").length > 3
  ) {
    structuredData.push({
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: page.metaTitle,
      author: {
        "@type": "Organization",
        name: "unprinted",
        url: process.env.GATSBY_SITE_URL,
      },
      url: `${process.env.GATSBY_SITE_URL}${page.url.pathname}`,
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `${process.env.GATSBY_SITE_URL}${page.url.pathname}`,
      },
      image: {
        "@type": "ImageObject",
        url:
          page.imageUrls?.cover?.publicURL ||
          `${process.env.GATSBY_SITE_URL}/images/ogp.png`,
        width: "2400px",
        height: "1260px",
      },
      datePublished: page.releasedAt?.toISO() || page.updatedAt.toISO(),
      dateModified: page.updatedAt.toISO(),
      publisher: {
        "@type": "Organization",
        name: "unprinted",
        url: process.env.GATSBY_SITE_URL,
      },
    });
  }

  // Jobs
  if (
    ["/jobs"].indexOf(directory) != -1 &&
    page.url.pathname.split("/").length > 3 &&
    jobPost
  ) {
    structuredData.push({
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: jobPost.title,
      description: feedContent(jobPost.content!, jobPost.references),
      identifier: {
        "@type": "PropertyValue",
        name: jobPost.company.name,
        value: jobPost.company.id,
      },
      datePosted: page.releasedAt?.toISO(),
      hiringOrganization: {
        "@type": "Organization",
        name: jobPost.company.name,
        sameAs: jobPost.company.links.filter(
          (link: any) => (link.type = "HP")
        )![0].url,
        logo: jobPost.company.logo.publicUrl,
      },
      jobLocation: {
        "@type": "Place",
        address: {
          "@type": "PostalAddress",
          addressLocality: jobPost.locations[0].name,
          addressRegion: jobPost.locations[0].name,
          addressCountry: "JP",
        },
      },
      baseSalary: {
        "@type": "MonetaryAmount",
        currency: "JPY",
        value: {
          "@type": "QuantitativeValue",
          minValue: jobPost.minimumSalary! * 10000,
          maxValue: jobPost.maximumSalary! * 10000,
          unitText: "YEAR",
        },
      },
    });
  }

  return structuredData;
};

export default structuredData;
